/**
 * Custom hook for V2 drug-aggregated search with React Query caching
 * Uses evidence-driven RAG search with drug-level aggregation
 */
import { useQuery } from '@tanstack/react-query';
import { authApi } from '../services/authApi';
import type { DrugAggregatedResponseV2 } from '../types';

interface SearchOptionsV2Drugs {
  top_k?: number;
  drug_filter?: string;
  section_filter?: string[];
  marketingCategories?: string[];
  /** Multi-region filter. When non-empty, sent to backend as ``regions``. */
  regions?: string[];
  /** Legacy single-region filter; ignored when ``regions`` is non-empty. */
  region?: string | undefined;
  page?: number;
  page_size?: number;
  /** Document Type the caller is currently on.  NOT sent to the backend
   *  (this endpoint is labels-only by contract — letter searches go through
   *  ``/api/fda-letters/search``).  Included so the React Query cache key
   *  differs across modes: without it, a prior labels submission cached
   *  under (query="warning", …) would be reused when the caller reruns the
   *  same query in ``'fda_letters'`` mode, leaking drug-shaped data into
   *  the letter render path via ``v2Results``.  Defaults to ``'labels'``
   *  for callers that don't pass it. */
  searchMode?: string;
  /** When true, includes draft label corpus results in the response. */
  include_label_negotiations?: boolean;
  /** Draft hit scope: 'all' (draft content + comments, default) | 'comments_only'. */
  draft_comment_scope?: 'all' | 'comments_only';
  /** Search corpus. Omitted/'labels' -> request body unchanged. */
  corpus?: 'labels' | 'ha_reviews';
  /** HA document types; sent only when ``corpus === 'ha_reviews'``. */
  review_doc_types?: string[];
}

/**
 * Hook that wraps the V2 drug-aggregated search API with React Query caching.
 *
 * Cache behavior:
 * - Results are cached for 5 minutes (staleTime)
 * - Inactive cache entries persist for 30 minutes (gcTime)
 * - On back navigation, cached results are shown immediately
 * - Page 1 runs the full pipeline; pages 2+ are instant (served from Redis cache)
 */
export function useSearchQueryV2Drugs(
  query: string,
  options: SearchOptionsV2Drugs = {},
  enabled: boolean = true
) {
  const {
    top_k = 50,
    drug_filter,
    section_filter,
    marketingCategories = ['nda', 'bla', 'nda_ag'],
    regions,
    region,
    page = 1,
    page_size = 50,
    searchMode = 'labels',
    include_label_negotiations,
    draft_comment_scope,
    corpus,
    review_doc_types,
  } = options;
  const isHa = corpus === 'ha_reviews';

  // Stable query-key fragment for regions so order doesn't fragment the cache.
  const regionsKey = regions?.length ? [...regions].sort().join(',') : null;
  const docTypesKey = isHa && review_doc_types?.length ? [...new Set(review_doc_types)].sort().join(',') : null;

  return useQuery<DrugAggregatedResponseV2 | null>({
    // ``searchMode`` is part of the key (NOT the body) so the labels-mode
    // cache doesn't collide with a fda_letters-mode caller passing the
    // same query string.  See SearchOptionsV2Drugs.searchMode for the
    // bug this defends against.
    queryKey: ['search-v2-drugs', query, { top_k, drug_filter, section_filter, marketingCategories, regionsKey, region, page, page_size, searchMode, include_label_negotiations: include_label_negotiations ?? false, draft_comment_scope: draft_comment_scope ?? 'all', corpus: isHa ? 'ha_reviews' : 'labels', docTypesKey }],
    queryFn: async () => {
      if (!query.trim()) {
        return null;
      }
      const response = await authApi.searchV2Drugs(query, {
        top_k,
        marketingCategories,
        page,
        page_size,
        ...(drug_filter && { drug_filter }),
        ...(section_filter && { section_filter }),
        ...(regions?.length ? { regions } : (region ? { region } : {})),
        ...(isHa
          ? { corpus: 'ha_reviews' as const, ...(review_doc_types?.length ? { review_doc_types } : {}) }
          : include_label_negotiations ? { include_label_negotiations: true } : {}),
        ...(draft_comment_scope && draft_comment_scope !== 'all' ? { draft_comment_scope } : {}),
      });
      return response as DrugAggregatedResponseV2;
    },
    enabled: enabled && query.trim().length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export default useSearchQueryV2Drugs;
